/**
 * BookingSummary Component
 *
 * Sticky sidebar showing listing info and price breakdown.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { PricingBreakdown } from './PricingBreakdown';
import type { BookingPricing } from '../hooks/usePricing';

interface ListingInfo {
  id: string;
  title: string;
  propertyType: string;
  photo: string;
  location: {
    city: string;
    region: string;
  };
  rating?: number;
  reviewCount?: number;
}

interface BookingSummaryProps {
  listing: ListingInfo;
  checkIn: Date | null;
  checkOut: Date | null;
  pricing: BookingPricing | null;
}

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const BookingSummary: React.FC<BookingSummaryProps> = ({
  listing,
  checkIn,
  checkOut,
  pricing,
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      {/* Listing Preview */}
      <div className="flex gap-4 pb-6 border-b border-gray-200">
        <Link to={`/listing/${listing.id}`} className="flex-shrink-0">
          <img
            src={listing.photo}
            alt={listing.title}
            className="w-24 h-24 object-cover rounded-lg"
          />
        </Link>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-500 capitalize">{listing.propertyType}</p>
          <Link
            to={`/listing/${listing.id}`}
            className="font-medium text-gray-900 hover:underline line-clamp-2"
          >
            {listing.title}
          </Link>
          <p className="text-sm text-gray-500 mt-1">
            {listing.location.city}, {listing.location.region}
          </p>

          {/* Rating */}
          {listing.rating && (
            <div className="flex items-center gap-1 mt-1">
              <StarIcon className="w-4 h-4 text-primary-500" />
              <span className="text-sm font-medium">{listing.rating.toFixed(2)}</span>
              {listing.reviewCount && (
                <span className="text-sm text-gray-500">
                  ({listing.reviewCount} reviews)
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Trip Details */}
      {checkIn && checkOut && (
        <div className="py-6 border-b border-gray-200">
          <h3 className="font-semibold mb-4">Your trip</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Check-in</span>
              <span className="font-medium">{formatDate(checkIn)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Check-out</span>
              <span className="font-medium">{formatDate(checkOut)}</span>
            </div>
            {pricing && (
              <div className="flex justify-between">
                <span className="text-gray-600">Duration</span>
                <span className="font-medium">
                  {pricing.nights} night{pricing.nights !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Price Breakdown */}
      {pricing && (
        <div className="pt-6">
          <h3 className="font-semibold mb-4">Price details</h3>
          <PricingBreakdown pricing={pricing} showDetails={true} />
        </div>
      )}

      {/* No Pricing Yet */}
      {!pricing && (
        <div className="pt-6 text-center text-gray-500">
          <p>Select dates to see the total price</p>
        </div>
      )}

      {/* Guarantees */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-start gap-3 text-sm">
          <ShieldIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-gray-900">Secure booking</p>
            <p className="text-gray-500">
              Your payment is protected. If there's a problem, we'll help.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Star Icon
const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// Shield Icon
const ShieldIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

export default BookingSummary;
